import type { EventDropPoint } from '../types';
import { isAllowedGoogleMapsUrl } from './google-maps-url';

export const MAX_DROP_POINTS = 20;
export const DROP_POINT_LABEL_MAX = 80;
export const DROP_POINT_ADDRESS_MAX = 300;

export const DROP_POINT_MAPS_ERROR =
  'Drop point maps links must be https URLs on google.com/maps, maps.app.goo.gl, or goo.gl/maps';

function cleanAddress(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/** Read stored JSON. Invalid entries are skipped; a bad maps link is dropped, not the address. */
export function parseDropPointsJson(json: string | null | undefined): EventDropPoint[] {
  if (!json) return [];
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(value)) return [];

  const points: EventDropPoint[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as Record<string, unknown>;
    const address = typeof raw.address === 'string' ? cleanAddress(raw.address) : '';
    if (!address) continue;
    const labelRaw = typeof raw.label === 'string' ? raw.label.trim() : '';
    const mapsRaw = typeof raw.mapsUrl === 'string' ? raw.mapsUrl.trim() : '';
    points.push({
      label: labelRaw ? labelRaw.slice(0, DROP_POINT_LABEL_MAX) : null,
      address: address.slice(0, DROP_POINT_ADDRESS_MAX),
      mapsUrl: mapsRaw && isAllowedGoogleMapsUrl(mapsRaw) ? mapsRaw : null,
    });
  }
  return points;
}

export function validateDropPoints(
  input: unknown,
): { ok: true; points: EventDropPoint[] } | { ok: false; error: string } {
  if (!Array.isArray(input)) return { ok: false, error: 'dropPoints must be a list' };
  if (input.length > MAX_DROP_POINTS) {
    return { ok: false, error: `An event can have at most ${MAX_DROP_POINTS} drop points` };
  }

  const points: EventDropPoint[] = [];
  for (const item of input) {
    if (!item || typeof item !== 'object') return { ok: false, error: 'Each drop point must include an address' };
    const raw = item as Record<string, unknown>;
    const labelRaw = typeof raw.label === 'string' ? raw.label.trim() : '';
    if (labelRaw.length > DROP_POINT_LABEL_MAX) {
      return { ok: false, error: `Drop point labels must be ${DROP_POINT_LABEL_MAX} characters or fewer` };
    }
    const address = typeof raw.address === 'string' ? cleanAddress(raw.address) : '';
    if (!address) return { ok: false, error: 'Each drop point needs an address' };
    if (address.length > DROP_POINT_ADDRESS_MAX) {
      return { ok: false, error: `Drop point addresses must be ${DROP_POINT_ADDRESS_MAX} characters or fewer` };
    }

    let mapsUrl: string | null = null;
    if (raw.mapsUrl != null && raw.mapsUrl !== '') {
      if (typeof raw.mapsUrl !== 'string') return { ok: false, error: DROP_POINT_MAPS_ERROR };
      const trimmed = raw.mapsUrl.trim();
      if (trimmed) {
        if (!isAllowedGoogleMapsUrl(trimmed)) return { ok: false, error: DROP_POINT_MAPS_ERROR };
        mapsUrl = trimmed;
      }
    }

    points.push({ label: labelRaw || null, address, mapsUrl });
  }

  return { ok: true, points };
}
